import { useMemo } from 'react';
import { usePlansByIdsQuery } from '@coverright/data-access/medicare';
import { GetDefaultPlansYear } from '@coverright/utils';
import { PlanYear } from '@coverright/data-access/types/medicare';
import { useAi } from '../ai-context';
import { useMedicareQuote } from './medicare/use-medicare-quote';
import type { MAPlanWithInsights } from '../../pages/plans/simple/ui/ma-plan-card';
import type { MedigapPlanWithInsights } from '../../pages/plans/simple/ui/medigap-plan-card';
import { useMedigapCarrierLookup } from './medigap/use-medigap-carrier-lookup';

export type AnyPlanWithInsights = MAPlanWithInsights | MedigapPlanWithInsights;

/**
 * Returns enriched plans for the given selectedType:
 * - 'ma'          → recommendations.ma.recommendations, org_name/parent_org_name overridden from GraphQL
 * - 'medigap_pdp' → recommendations.medigap.recommendations, company names overridden from GraphQL by NAIC
 */
export function useEnrichedPlans(selectedType: 'ma' | 'medigap_pdp' = 'ma') {
  const { recommendations, highlights, ...rest } = useAi();
  const quote = useMedicareQuote();

  const maRaw = useMemo(
    () => (recommendations?.ma?.recommendations ?? []).slice(0, 3),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recommendations?.ma?.recommendations?.map(p => p.bid_id).join(',')],
  );

  const mgRaw = useMemo(
    () => (recommendations?.medigap?.recommendations ?? []).slice(0, 3),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recommendations?.medigap?.recommendations?.map(p => p.plan_letter).join(',')],
  );

  const planInputs = useMemo(
    () =>
      selectedType === 'ma'
        ? maRaw.map(p => ({
            bidId: p.bid_id,
            planYear: GetDefaultPlansYear() as PlanYear,
            zip: quote?.zip ?? '',
            county: quote?.county ?? '',
            fullYear: false,
          }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedType, maRaw.map(p => p.bid_id).join(','), quote?.zip, quote?.county],
  );

  const { data } = usePlansByIdsQuery({
    variables: { plans: planInputs, quoteId: quote?.id },
    skip: !planInputs.length || !quote?.zip,
  });

  const logoMap = useMemo(() => {
    const map: Record<string, { org_name: string | null; parent_org_name: string | null }> = {};
    for (const p of data?.plansByIds ?? []) {
      map[p.bidId] = { org_name: p.orgName ?? null, parent_org_name: p.parentOrgName ?? null };
    }
    return map;
  }, [data]);

  // Enrich medigap carrier names — plan engine API returns incorrect company names
  const carrierMap = useMedigapCarrierLookup(mgRaw);

  const plans = useMemo((): AnyPlanWithInsights[] => {
    if (selectedType === 'ma') {
      return maRaw.map(p => ({
        ...p,
        why_this_plan_works: highlights?.plans?.find(ph => ph.bid_id === p.bid_id)?.why_this_plan_works,
        things_to_consider: highlights?.plans?.find(ph => ph.bid_id === p.bid_id)?.things_to_consider,
        ...(logoMap[p.bid_id] ?? {}),
      })) as MAPlanWithInsights[];
    }
    return mgRaw.map(p => ({
      ...p,
      bid_id: p.plan_letter,
      why_this_plan_works: highlights?.plans?.find(ph => ph.bid_id === p.plan_letter)?.why_this_plan_works,
      things_to_consider: highlights?.plans?.find(ph => ph.bid_id === p.plan_letter)?.things_to_consider,
      recommended_carrier: {
        ...p.recommended_carrier,
        ...(carrierMap[p.recommended_carrier.naic] ?? {}),
      },
      all_carriers: p.all_carriers.map(c => ({
        ...c,
        ...(carrierMap[c.naic] ?? {}),
      })),
    })) as MedigapPlanWithInsights[];
  }, [selectedType, maRaw, logoMap, mgRaw, carrierMap, highlights]);

  return { plans, recommendations, ...rest };
}
